import { useState } from 'react';
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFounderImages } from '@/hooks/useFounderImages';

/** Respaldo: el asset estático histórico de la marca (RF-4 / RF-5). */
const DEFAULT_FOUNDER_IMAGE = '/founder.png';

const WHATSAPP_URL =
  "https://wa.me/50761568575?text=" +
  encodeURIComponent(
    "¡Hola Vivi! Vi tu historia en la página y quiero hacer un pedido 😊"
  );

interface FounderSectionProps {
  /** Variante compacta: banda horizontal para la home */
  compact?: boolean;
}

/**
 * Sección Fundadora — señal humana de confianza para la marca.
 *
 * Intent: intimidad editorial + credibilidad artesanal. El marco en arco
 * (rounded-t-[10rem]) es la firma visual de esta sección: evoca papel y
 * lo hecho a mano; solo tiene sentido en una marca artesanal.
 */
export const FounderSection = ({ compact = false }: FounderSectionProps) => {
  const founderImages = useFounderImages();
  const [failedSrc, setFailedSrc] = useState<{
    about?: string;
    compact?: string;
  }>({});

  // Se recuerda la URL que falló, no un booleano: si llega una nueva, el
  // componente la reintenta solo en vez de quedar pegado al default.
  const aboutUrl = founderImages?.imageAbout || DEFAULT_FOUNDER_IMAGE;
  const compactUrl = founderImages?.imageCompact || DEFAULT_FOUNDER_IMAGE;
  const aboutSrc =
    failedSrc.about === aboutUrl ? DEFAULT_FOUNDER_IMAGE : aboutUrl;
  const compactSrc =
    failedSrc.compact === compactUrl ? DEFAULT_FOUNDER_IMAGE : compactUrl;

  if (compact) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
          <a
            href="/about"
            className="group flex flex-col sm:flex-row items-center gap-6 max-w-2xl mx-auto text-center sm:text-left"
          >
            <img
              src={compactSrc}
              alt="Viviana Piedrahita, fundadora de Vivi con Amor y Arte"
              className="w-20 h-20 rounded-full object-cover object-top shadow-md ring-2 ring-primary/40 group-hover:ring-primary transition-all duration-300 shrink-0"
              onError={() => setFailedSrc((prev) => ({ ...prev, compact: compactUrl }))}
            />
            <div>
              <p className="font-playfair italic text-lg md:text-xl text-brown-light leading-relaxed">
                "Cada agenda que sale de mis manos lleva un pedacito de mi
                historia."
              </p>
              <p className="mt-3 text-sm font-medium text-primary-dark tracking-wide">
                Viviana Piedrahita · Fundadora{" "}
                <span className="text-brown-light/70 group-hover:text-brown transition-colors">
                  → Conocer nuestra historia
                </span>
              </p>
            </div>
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-white via-peach/20 to-background overflow-hidden">
      {/* Cita decorativa gigante de fondo */}
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-6 left-1/2 -translate-x-1/2 font-playfair text-[10rem] md:text-[18rem] leading-none text-primary/10"
      >
        "
      </span>

      <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Kicker */}
        <div className="flex flex-col items-center mb-14 md:mb-20">
          <span className="text-primary-dark font-medium tracking-widest uppercase text-sm">
            La Fundadora
          </span>
          <div className="w-24 h-1 bg-primary mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-20 lg:gap-28 items-center max-w-6xl mx-auto">
          {/* Retrato con marco arco */}
          <div className="relative flex justify-center md:justify-start">
            {/* Marco decorativo offset */}
            <div
              aria-hidden="true"
              className="absolute top-3 -left-3 w-full h-full rounded-t-[12rem] rounded-b-2xl border border-primary/40"
            ></div>
            <img
              src={aboutSrc}
              alt="Viviana Piedrahita, fundadora de Vivi con Amor y Arte, escribiendo en su taller"
              className="relative w-full max-w-md lg:max-w-lg aspect-[4/5] object-cover object-top rounded-t-[12rem] rounded-b-2xl shadow-xl"
              loading="lazy"
              onError={() => setFailedSrc((prev) => ({ ...prev, about: aboutUrl }))}
            />
          </div>

          {/* Contenido */}
          <div className="text-center md:text-left space-y-7 pb-16 md:pb-0">
            <div className="space-y-3">
              <h2 className="font-playfair font-bold text-4xl sm:text-5xl md:text-6xl text-brown leading-tight">
                Viviana Piedrahita
              </h2>
              <p className="text-sm font-medium text-primary-dark tracking-widest uppercase">
                Fundadora · Vivi con Amor y Arte
              </p>
            </div>

            <blockquote className="font-playfair italic text-2xl md:text-3xl text-brown-light leading-relaxed pr-16 md:pr-0">
              "Cada agenda que sale de mis manos lleva un pedacito de mi
              historia."
            </blockquote>

            <div className="space-y-5 text-lg text-brown-light/90 leading-relaxed max-w-xl mx-auto md:mx-0 pr-2 md:pr-0">

              <p>
                Lo que comenzó como un proyecto personal se convirtió en un
                negocio familiar dedicado a crear agendas únicas. Detrás de
                cada diseño hay horas de dedicación, atención al detalle y
                mucho amor por lo hecho a mano.
              </p>
              <p>
                Cuando compras una agenda Vivi, no llevas solo un producto:
                te llevas una pieza creada por manos reales, con propósito y
                corazón.
              </p>
            </div>

            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-primary text-brown hover:bg-primary hover:text-brown-dark hover:border-primary font-semibold rounded-full px-10 py-4 min-h-[52px] text-base touch-manipulation transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5 mr-2" />
                Escríbele directamente a Vivi
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
